import React, { useState, useCallback, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';

export interface MapLocation {
  title: string;
  location: string;
  lat: number;
  lng: number;
  dayNumber: number;
  time?: string;
}

interface ItineraryMapProps {
  locations: MapLocation[];
  center: { lat: number; lng: number };
  zoom?: number;
  themeColor: string;
  totalDays: number;
  dayLabels?: string[];
}

const DAY_COLORS = [
  '#e8443a', // Day 1 — red
  '#f59e0b', // Day 2 — amber
  '#40916c', // Day 3 — green
  '#4a9fcc', // Day 4 — blue
  '#a855f7', // Day 5 — purple
  '#ec4899', // Day 6 — pink
  '#14b8a6', // Day 7 — teal
  '#f97316', // Day 8 — orange
  '#6366f1', // Day 9 — indigo
  '#84cc16', // Day 10 — lime
];

function MarkerPin({ dayNumber, title }: { dayNumber: number; title: string }) {
  const color = DAY_COLORS[(dayNumber - 1) % DAY_COLORS.length];
  return (
    <div
      title={title}
      style={{
        width: 28,
        height: 28,
        borderRadius: '50% 50% 50% 0',
        background: color,
        transform: 'rotate(-45deg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        border: '2px solid rgba(255,255,255,0.9)',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          transform: 'rotate(45deg)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {dayNumber}
      </span>
    </div>
  );
}

function MapContent({
  locations,
  center,
  zoom,
}: {
  locations: MapLocation[];
  center: { lat: number; lng: number };
  zoom: number;
}) {
  const map = useMap();
  const [activeId, setActiveId] = useState<number | null>(null);

  useEffect(() => {
    if (!map || locations.length === 0) return;
    if (locations.length === 1) {
      map.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
      map.setZoom(15);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    locations.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, [map, locations, center, zoom]);

  return (
    <>
      {locations.map((loc, i) => (
        <AdvancedMarker
          key={`${loc.dayNumber}-${loc.title}-${i}`}
          position={{ lat: loc.lat, lng: loc.lng }}
          onClick={() => setActiveId(activeId === i ? null : i)}
        >
          <MarkerPin dayNumber={loc.dayNumber} title={loc.title} />
        </AdvancedMarker>
      ))}
      {activeId !== null && locations[activeId] && (
        <InfoWindow
          position={{
            lat: locations[activeId].lat,
            lng: locations[activeId].lng,
          }}
          onCloseClick={() => setActiveId(null)}
          pixelOffset={[0, -35]}
        >
          <div style={{ padding: '2px 4px', maxWidth: 220 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 2,
                color: '#1a1a1a',
              }}
            >
              {locations[activeId].title}
            </div>
            <div style={{ fontSize: 11, color: '#555', marginBottom: 2 }}>{locations[activeId].location}</div>
            {locations[activeId].time && (
              <div style={{ fontSize: 11, color: '#888' }}>
                Day {locations[activeId].dayNumber} &middot; {locations[activeId].time}
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
}

export function buildMapLocations(
  days: {
    dayNumber: number;
    activities: { title: string; location?: string; time?: string; muted?: boolean }[];
  }[],
  coordinates: Record<string, { lat: number; lng: number }>
): MapLocation[] {
  const locations: MapLocation[] = [];
  for (const day of days) {
    for (const activity of day.activities) {
      if (activity.location && coordinates[activity.location]) {
        locations.push({
          title: activity.title,
          location: activity.location,
          lat: coordinates[activity.location].lat,
          lng: coordinates[activity.location].lng,
          dayNumber: day.dayNumber,
          time: activity.time,
        });
      }
    }
  }
  return locations;
}

const ItineraryMap: React.FC<ItineraryMapProps> = ({
  locations,
  center,
  zoom = 13,
  themeColor,
  totalDays,
  dayLabels,
}) => {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const filtered = selectedDay === null ? locations : locations.filter((loc) => loc.dayNumber === selectedDay);

  const handleTabClick = useCallback((day: number | null) => {
    setSelectedDay(day);
  }, []);

  if (!apiKey) {
    return (
      <div className="map-no-key">
        <p>
          Add <code>VITE_GOOGLE_MAPS_API_KEY</code> to your <code>.env</code> to enable interactive maps.
        </p>
      </div>
    );
  }

  return (
    <section className="itinerary-map-section">
      <h2 className="section-title" style={{ color: themeColor }}>
        Map
      </h2>

      <div className="map-day-tabs">
        <button
          className={`map-day-tab ${selectedDay === null ? 'map-day-tab--active' : ''}`}
          style={
            selectedDay === null
              ? { background: themeColor, borderColor: themeColor, color: '#fff' }
              : { borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' }
          }
          onClick={() => handleTabClick(null)}
        >
          All
        </button>
        {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => {
          const isActive = selectedDay === day;
          const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
          return (
            <button
              key={day}
              className={`map-day-tab ${isActive ? 'map-day-tab--active' : ''}`}
              style={
                isActive
                  ? { background: color, borderColor: color, color: '#fff' }
                  : { borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' }
              }
              onClick={() => handleTabClick(day)}
            >
              {dayLabels ? dayLabels[day - 1] : `Day ${day}`}
            </button>
          );
        })}
      </div>

      <div className="map-container">
        <APIProvider apiKey={apiKey}>
          <Map
            defaultCenter={center}
            defaultZoom={zoom}
            mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'}
            gestureHandling="cooperative"
            disableDefaultUI={true}
            zoomControl={true}
            colorScheme="DARK"
            style={{ width: '100%', height: '100%' }}
          >
            <MapContent locations={filtered} center={center} zoom={zoom} />
          </Map>
        </APIProvider>
      </div>
    </section>
  );
};

export default ItineraryMap;
